import { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import useRefreshToken from "../hooks/useRefreshToken";
import useAuth from "../hooks/useAuth";
import Loader from "./Loader";
import Header from "./Header";
import SideBar from "./SideBar";
import ToastManager from "./ToastManager";
import { useDispatch, useSelector } from "react-redux";
import {
  selectActiveWinery,
  selectLabels,
  selectUserSockets,
  selectWineries,
} from "../context/selectors";
import { setUserSockets } from "../context/userSockets/userSocketsSlice";
import { toast } from "react-toastify";
import useLabels from "../hooks/useLabels";
import { getItem, setItem } from "../services/LocalStorage";
import Footer from "./Footer";
import CookieBar from "./CookieBar";
import { NotificationWithLink } from "./Generic";
import NotesButton from "./FloatingNotes/NotesButton";
import { removeOp } from "../context/operations/operationsSlice";
import { addDays } from "date-fns";

const PersistentLogin = ({ isMobile, socket }) => {
  // const [getLabel, ready] = useLabels();
  const [getLabel, getArea, setLabels, ready] = useLabels();

  const [isLoading, setIsLoading] = useState(true);
  const refresh = useRefreshToken();
  const { auth, setAuth } = useAuth();
  const dispatch = useDispatch();
  const wineries = useSelector(selectWineries);
  const activeWinery = useSelector(selectActiveWinery);
  const userSockets = useSelector(selectUserSockets);
  // const isLabelsLoaded = useSelector(selectLabels);
  const navigate = useNavigate();
  const location = useLocation();
  // socket.io
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [isIcon, setIsIcon] = useState(getItem("sidebar_icon") || false);

  const customIdExpiringPwd = "custom-id-exp-pwd";
  useEffect(() => {
    if (
      !auth?.accessToken ||
      !activeWinery?.id ||
      !ready
    ) return;

    if (!auth?.password_expire_date) return;
    const expiring = addDays(new Date(), 10) > new Date(auth.password_expire_date);
    if (expiring)
      toast.warning(getLabel("toast_WarningIsPasswordExpiring"), {
        toastId: customIdExpiringPwd,
        position: toast.POSITION.BOTTOM_RIGHT,
        exclude: true,
        autoClose: false,
        onClick: () => navigate(`/users/${auth.user_id}/update`),
      });
  }, [
    auth?.accessToken,
    auth?.password_expire_date,
    activeWinery?.id,
    ready
  ]);

  useEffect(() => {
    let isMounted = true;

    const verifyRefreshToken = async () => {
      try {
        await refresh();
      } catch (err) {
        console.error(err);
      } finally {
        if (auth?.role === 1) {
          navigate("/clients");
          setIsLoading(false);
        } else {
          if (!activeWinery) {
            if (!wineries || wineries?.length === 0) {
              navigate("/");
              // navigate('/wineries/new');
              setIsLoading(false);
              return;
            }
            navigate("/#wineriesList");
          } else {
            navigate(location.pathname);
          }
        }
        isMounted && setIsLoading(false);
      }
    };

    if (!auth?.accessToken) {
      verifyRefreshToken();
    } else {
      if (auth?.role === 1) {
        navigate("/clients");
        setIsLoading(false);
      } else {
        if (!activeWinery && auth?.role === 2) {
          if (!wineries || wineries?.length === 0) {
            navigate("/wineries/new");
            setIsLoading(false);
            const customIdNoWineries = "custom-id-no-wineries";
            toast.info(getLabel("toast_missingWineries"), {
              toastId: customIdNoWineries,
              position: toast.POSITION.BOTTOM_RIGHT,
              exclude: true,
              autoClose: 5000,
            });
            // return;
          }
          navigate("/#wineriesList");
          setIsLoading(false);
        } else {
          navigate(location.pathname);
          setIsLoading(false);
        }
      }
    }

    if (!auth?.user_id || !activeWinery) return;

    // socket connection
    const username = auth.username;
    const client_name = auth.client_name;

    socket.auth = {
      username,
      id: auth?.user_id,
      client_name: client_name,
      wineryId: activeWinery?.id,
    };
    const sessionId = getItem("sessionId");
    if (sessionId) {
      socket.auth = { ...socket.auth, sessionId };
    }

    if (!socket.connected) {
      socket.connect();
    }

    return () => (isMounted = false);
  }, [auth?.user_id, activeWinery?.id]);

  useEffect(() => {
    const delay = 5000;
    const timeout = setTimeout(() => {
      if (!ready) {
        return;
      }

      // Fetch notifications
      socket.emit("fetch notifications");

      // Listen for notifications
    }, delay);

    return () => clearTimeout(timeout);
  }, [ready]);

  // socket.io
  useEffect(() => {
    function onConnect() {
      console.log("connect");
      setIsConnected(true);
      reconnecting = false; // reset flag quando connesso

      // Re-register user after reconnect
      socket.emit("register user", {
        id: auth?.user_id,
        username: auth?.username,
        client_name: auth?.client_name,
        wineryId: activeWinery?.id,
        sessionId: getItem("sessionId"),
      });
    }

    function onReconnectFailed() {
      console.log("Reconnection failed");
    }

    function onReconnectAttempt() {
      // On reconnect attempt (after server restart)
      console.log("Reconnect attempt");
      socket.auth = {
        username: auth.username,
        id: auth?.user_id,
        client_name: auth.client_name,
        wineryId: activeWinery?.id,
        sessionId: getItem("sessionId"),
      };
    }

    let reconnecting = false;

    function onDisconnect(reason) {
      console.log("disconnect");
      setIsConnected(false);

      if (reason === "io server disconnect" && !reconnecting && auth?.user_id) {
        reconnecting = true;
        // Il server ha forzato la disconnessione: es. restart
        // Serve aggiornare auth PRIMA di riconnettere
        socket.auth = {
          username: auth.username,
          id: auth?.user_id,
          client_name: auth.client_name,
          wineryId: activeWinery?.id,
          sessionId: getItem("sessionId"),
        };
        socket.connect();
      }

      const index = userSockets?.findIndex((u) => u?.self);

      if (index === -1) {
        return;
      }
      dispatch(
        setUserSockets((prevState) => [
          ...prevState?.slice(0, index),
          {
            ...prevState[index],
            connected: false,
            ws: undefined, // remove ws property because is not serializable
          },
          ...prevState?.slice(index + 1),
        ])
      );
    }

    function onConnectError(err) {
      console.log("onConnectError", err);

      if (err.message === "invalid username") {
        console.log("usernameAlreadySelected", err);
      }
      // console.log(auth);

      // retry connection
      setTimeout(() => {
        // socket connection
        const username = auth.username;
        const client_name = auth.client_name;
        socket.auth = {
          username,
          id: auth?.user_id,
          client_name: client_name,
          wineryId: activeWinery?.id,
        };
        const sessionId = getItem("sessionId");
        if (sessionId) {
          socket.auth = { ...socket.auth, sessionId };
        }
        console.log("onConnectError timeout");

        socket.connect();
      }, 5000);
    }

    function onSession({ sessionId, userId }) {
      // attach the session ID to the next reconnection attempts
      socket.auth = { sessionId };
      // store it in the localStorage
      setItem("sessionId", sessionId);
      // save the ID of the user
      socket.userId = userId;
      // console.log("session =============================================");
    }

    function onUsers(users) {
      // console.log("users =============================================", users);
      const userSocketsTmp = [...userSockets];

      for (const user of users) {
        user.notifications?.forEach((notification) => {
          notification.fromSelf = notification.from === socket.userID;
          if (!notification.fromSelf) {
            // onNotificationEvent(notification);
          }
        });

        const userIndex = userSocketsTmp?.findIndex((u) => u?.id === user?.id);
        if (userIndex === -1) {
          // add new connected user
          user.self = user.userID === socket.userID;
          userSocketsTmp.push(user);
        } else {
          // update userId
          const tmpUser = { ...userSocketsTmp[userIndex], ws: undefined };
          userSocketsTmp[userIndex] = tmpUser;
        }
      }

      // put the current user first, and sort by username
      userSocketsTmp?.sort((a, b) => {
        if (a.self) return -1;
        if (b.self) return 1;
        if (a.username < b.username) return -1;
        return a.username > b.username ? 1 : 0;
      });

      dispatch(setUserSockets(userSocketsTmp));
      // console.log(userSocketsTmp, "userSockets");
    }

    function onUserConnected(user) {
      const userSocketsTmp = [...userSockets];

      const userIndex = userSocketsTmp?.findIndex((u) => u?.id === user?.id);
      if (userIndex === -1) {
        // add new connected user
        user.self = user.userID === socket.userID;
        user.connected = true;
        userSocketsTmp.push(user);
      } else {
        // update userId
        const tmpUser = { ...userSocketsTmp[userIndex] };
        tmpUser.connected = true;
        userSocketsTmp[userIndex] = tmpUser;
      }
      // console.log("userSockets user connected");
    }

    function onUserDisonnected(id) {
      const userSocketsTmp = [...userSockets];

      const userIndex = userSocketsTmp?.findIndex((u) => u?.id === id);
      if (userIndex > -1) {
        // update userId
        const tmpUser = { ...userSocketsTmp[userIndex] };
        tmpUser.connected = false;
        userSocketsTmp[userIndex] = tmpUser;
      }
      // console.log("userSockets user disconnected");
    }

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("reconnect_attempt", onReconnectAttempt);
    socket.on("reconnect_failed", onReconnectFailed);
    socket.on("session", onSession);
    socket.on("users", onUsers);
    socket.on("user connected", onUserConnected);
    socket.on("user disconnected", onUserDisonnected);
    socket.on("connect_error", onConnectError);

    return () => {
      // socket.disconnect();

      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("reconnect_attempt", onReconnectAttempt);
      socket.off("reconnect_failed", onReconnectFailed);
      socket.off("session", onSession);
      socket.off("users", onUsers);
      socket.off("user connected", onUserConnected);
      socket.off("user disconnected", onUserDisonnected);
      socket.off("connect_error", onConnectError);
    };
  }, []);

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <Header isMobile={isMobile} isConnected={isConnected}></Header>
          <main className={`container ${isIcon ? "icon" : ""}`}>
            <SideBar
              isMobile={isMobile}
              isIcon={isIcon}
              setIsIcon={setIsIcon}
            ></SideBar>
            <ToastManager />
            <NotesButton />
            <Outlet />
          </main>
          <Footer isMobile={isMobile}></Footer>
          <CookieBar isMobile={isMobile}></CookieBar>
        </>
      )}
    </>
  );
};

export default PersistentLogin;
